/**
 * Volume Discount API - Product page integration
 * Fetches volume discount tiers from ctd-shipping.flogi.app and renders tiers + dynamic price.
 */
(function () {
  const VOLUME_DISCOUNT_API_BASE = 'https://ctd-shipping.flogi.app';

  function fetchVolumeDiscount(productGid, variantGid, signal) {
    const params = new URLSearchParams();
    if (variantGid) {
      params.append('variantId', variantGid);
    } else if (productGid) {
      params.append('productId', productGid);
    } else {
      return Promise.resolve({ hasDiscount: false, discounts: [] });
    }
    return fetch(`${VOLUME_DISCOUNT_API_BASE}/api/volume-discounts-info?${params.toString()}`, {
      signal,
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : { hasDiscount: false, discounts: [] }))
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.warn('Volume discount API error:', err);
        }
        return { hasDiscount: false, discounts: [] };
      });
  }

  function calculateDiscountedPrice(originalPrice, quantity, discountRules) {
    if (!discountRules || discountRules.length === 0) return originalPrice;
    const sortedRules = [...discountRules].sort((a, b) => b.minQuantity - a.minQuantity);
    const applicableRule = sortedRules.find((rule) => quantity >= rule.minQuantity);
    if (!applicableRule) return originalPrice;
    if (applicableRule.discountType === 'fixed_price') {
      return parseFloat(applicableRule.discountValue);
    }
    if (applicableRule.discountType === 'percentage') {
      const discountPercent = parseFloat(applicableRule.discountValue) / 100;
      return originalPrice * (1 - discountPercent);
    }
    return originalPrice;
  }

  function getApplicableRule(quantity, discountRules) {
    if (!discountRules || discountRules.length === 0) return null;
    const sortedRules = [...discountRules].sort((a, b) => b.minQuantity - a.minQuantity);
    return sortedRules.find((rule) => quantity >= rule.minQuantity) || null;
  }

  function formatPrice(value, currency) {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(num)) return '$0.00';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return `$${num.toFixed(2)}`;
    }
  }

  function formatPriceNoDecimals(value, currency) {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(num)) return '$0';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(num);
    } catch {
      return `$${Math.round(num)}`;
    }
  }

  function formatDiscountMessage(rule, originalPrice, currency) {
    const orig = parseFloat(originalPrice);
    if (rule.discountType === 'fixed_price') {
      const price = parseFloat(rule.discountValue);
      const savings = orig - price;
      return `Buy ${rule.minQuantity}+ for ${formatPrice(price, currency)} each${savings > 0 ? ` (Save ${formatPriceNoDecimals(savings, currency)})` : ''}`;
    }
    return `Buy ${rule.minQuantity}+ for ${rule.discountValue}% off`;
  }

  function formatTableCell(rule, currencyCode, eachItem) {
    if (rule.discountType === 'fixed_price') {
      return `${formatPrice(parseFloat(rule.discountValue), currencyCode)} ${eachItem || 'each item'}`;
    }
    return `${rule.discountValue}% off`;
  }

  function getStrings(container) {
    return {
      title: container?.dataset?.volumeDiscountTitle || 'Volume discounts',
      priceAtQuantity: container?.dataset?.volumeDiscountPriceAtQuantity || 'Price at this quantity',
      each: container?.dataset?.volumeDiscountEach || 'each',
      eachItem: container?.dataset?.volumeDiscountEachItem || 'each item',
      buyColumn: container?.dataset?.volumeDiscountBuyColumn || 'Buy',
      discountedPriceColumn: container?.dataset?.volumeDiscountDiscountedPriceColumn || 'Discounted Price',
      addForDiscount: container?.dataset?.volumeDiscountAddForDiscount || 'Add {0}+ for discounted price',
    };
  }

  function render(container, data, originalPrice, quantity, currency) {
    const { hasDiscount, discounts } = data;
    const strings = getStrings(container);
    container.innerHTML = '';
    if (!hasDiscount || !discounts || discounts.length === 0) return;

    const sortedRules = [...discounts].sort((a, b) => a.minQuantity - b.minQuantity);
    const currencyCode = currency || data.discounts?.[0]?.currency || 'USD';
    const compareAtPriceVal = container.dataset.compareAtPrice ? parseFloat(container.dataset.compareAtPrice) : NaN;
    const basePriceForDisplay = (compareAtPriceVal > 0 && !Number.isNaN(compareAtPriceVal)) ? compareAtPriceVal : originalPrice;

    const wrapper = document.createElement('div');
    wrapper.className = 'volume-discount-api';

    const titleEl = document.createElement('span');
    titleEl.className = 'volume-discount-api__title';
    titleEl.textContent = strings.title;
    wrapper.appendChild(titleEl);

    const table = document.createElement('table');
    table.className = 'volume-discount-api__table';
    table.setAttribute('role', 'table');
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    const thBuy = document.createElement('th');
    thBuy.scope = 'col';
    thBuy.textContent = strings.buyColumn;
    const thPrice = document.createElement('th');
    thPrice.scope = 'col';
    thPrice.textContent = strings.discountedPriceColumn;
    headerRow.appendChild(thBuy);
    headerRow.appendChild(thPrice);
    thead.appendChild(headerRow);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    sortedRules.forEach((rule) => {
      const tr = document.createElement('tr');
      const tdBuy = document.createElement('td');
      tdBuy.textContent = rule.minQuantity + '+';
      const tdPrice = document.createElement('td');
      tdPrice.textContent = formatTableCell(rule, currencyCode, strings.eachItem);
      tr.appendChild(tdBuy);
      tr.appendChild(tdPrice);
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    wrapper.appendChild(table);

    const quantityInputId = container.dataset.quantityInputId;
    const quantityInput = quantityInputId ? document.getElementById(quantityInputId) : null;
    const qty = quantity != null ? quantity : (quantityInput ? parseInt(quantityInput.value, 10) : 1);
    const applicableRule = getApplicableRule(qty, discounts);
    const discountedPrice = calculateDiscountedPrice(originalPrice, qty, discounts);
    const minQty = sortedRules[0]?.minQuantity ?? 2;
    const addForDiscountText = (strings.addForDiscount || 'Add {0}+ for discounted price')
      .replace('{0}', minQty)
      .replace(/\{\{\s*min_qty\s*\}\}/, minQty);

    const priceBlock = document.createElement('div');
    priceBlock.className = 'volume-discount-api__price';
    const priceLine = document.createElement('div');
    priceLine.className = 'volume-discount-api__price-line';
    if (applicableRule) {
      priceLine.innerHTML = `<s class="volume-discount-api__original">${formatPrice(basePriceForDisplay, currencyCode)}</s> <span class="volume-discount-api__current">${formatPrice(discountedPrice, currencyCode)} ${strings.each}</span>`;
      priceLine.style.display = '';
    } else {
      priceLine.textContent = addForDiscountText;
      priceLine.style.display = '';
    }
    priceBlock.appendChild(priceLine);
    const msgLine = document.createElement('div');
    msgLine.className = 'volume-discount-api__message caption';
    if (applicableRule) {
      msgLine.textContent = formatDiscountMessage(applicableRule, basePriceForDisplay, currencyCode);
      msgLine.style.display = '';
    } else {
      msgLine.style.display = 'none';
    }
    priceBlock.appendChild(msgLine);
    wrapper.appendChild(priceBlock);

    container.appendChild(wrapper);

    const priceContainerId = container.dataset.priceContainerId;
    const priceBlockEl = priceContainerId ? document.getElementById(priceContainerId) : null;
    const priceEl = priceBlockEl ? priceBlockEl.querySelector('.price') : null;
    const priceContainerInner = priceEl ? priceEl.querySelector('.price__container') : null;
    const saleBadgeScheme = container.dataset.saleBadgeScheme || '';

    function updateMainPrice(showDiscounted, discountedPriceVal, basePrice) {
      if (!priceEl || !priceContainerInner) return;
      const badge = priceEl.querySelector('.price__badge-sale');
      const priceForStrikethroughAndSavings = (basePrice != null && basePrice > 0) ? basePrice : originalPrice;
      if (showDiscounted) {
        if (!container._volumeDiscountOriginalPriceContainerHTML) {
          container._volumeDiscountOriginalPriceContainerHTML = priceContainerInner.innerHTML;
        }
        const savings = priceForStrikethroughAndSavings - discountedPriceVal;
        priceEl.classList.add('price--on-sale', 'price--volume-discount');
        priceContainerInner.innerHTML =
          '<div class="price__regular" style="display:none"></div>' +
          '<div class="price__sale" style="display: block;">' +
          '<span class="visually-hidden visually-hidden--inline">' + (strings.priceAtQuantity || 'Sale price') + '</span>' +
          '<s class="price-item price-item--regular">' + formatPrice(priceForStrikethroughAndSavings, currencyCode) + '</s> ' +
          '<span class="price-item price-item--sale price-item--last">' + formatPrice(discountedPriceVal, currencyCode) + '</span>' +
          '</div>';
        if (badge) {
          badge.textContent = 'Save ' + formatPriceNoDecimals(savings, currencyCode);
          badge.style.display = 'inline-flex';
        } else {
          let saveBadge = priceEl.querySelector('.volume-discount-save-badge');
          if (!saveBadge) {
            saveBadge = document.createElement('span');
            saveBadge.className = 'badge price__badge-sale volume-discount-save-badge color-' + saleBadgeScheme;
            saveBadge.setAttribute('style', 'min-width: 80px; height: 26px; padding: 4px 10.5px; border-radius: 4px; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;');
            priceEl.appendChild(saveBadge);
          }
          saveBadge.textContent = 'Save ' + formatPriceNoDecimals(savings, currencyCode);
          saveBadge.style.display = 'inline-flex';
        }
      } else {
        priceEl.classList.remove('price--volume-discount');
        if (container._volumeDiscountOriginalPriceContainerHTML) {
          priceContainerInner.innerHTML = container._volumeDiscountOriginalPriceContainerHTML;
        }
        if (badge) {
          badge.style.display = 'none';
        }
        const saveBadge = priceEl.querySelector('.volume-discount-save-badge');
        if (saveBadge) saveBadge.style.display = 'none';
      }
    }

    if (applicableRule) {
      updateMainPrice(true, discountedPrice, basePriceForDisplay);
    }

    function updatePriceAtQuantity() {
      const input = quantityInputId ? document.getElementById(quantityInputId) : null;
      const val = input ? parseInt(input.value, 10) : qty;
      const rule = getApplicableRule(val, discounts);
      const price = calculateDiscountedPrice(originalPrice, val, discounts);
      if (rule) {
        priceLine.innerHTML = `<s class="volume-discount-api__original">${formatPrice(basePriceForDisplay, currencyCode)}</s> <span class="volume-discount-api__current">${formatPrice(price, currencyCode)} ${strings.each}</span>`;
        priceLine.style.display = '';
        msgLine.textContent = formatDiscountMessage(rule, basePriceForDisplay, currencyCode);
        msgLine.style.display = '';
        updateMainPrice(true, price, basePriceForDisplay);
      } else {
        priceLine.textContent = addForDiscountText;
        priceLine.style.display = '';
        msgLine.style.display = 'none';
        updateMainPrice(false);
      }
    }

    if (quantityInput) {
      quantityInput.addEventListener('change', updatePriceAtQuantity);
      quantityInput.addEventListener('input', updatePriceAtQuantity);
      container._volumeDiscountQuantityCleanup = () => {
        quantityInput.removeEventListener('change', updatePriceAtQuantity);
        quantityInput.removeEventListener('input', updatePriceAtQuantity);
      };
    }
  }

  function loadAndRender(container, variantGid, productGid, originalPrice, currency, signal) {
    if (container._volumeDiscountAbort) {
      container._volumeDiscountAbort.abort();
    }
    container._volumeDiscountAbort = new AbortController();
    const ac = container._volumeDiscountAbort;
    if (container._volumeDiscountQuantityCleanup) {
      container._volumeDiscountQuantityCleanup();
      container._volumeDiscountQuantityCleanup = null;
    }
    container._volumeDiscountOriginalPriceContainerHTML = null;
    const price = parseFloat(originalPrice);
    const safePrice = Number.isNaN(price) ? 0 : price;

    container.innerHTML = '<span class="volume-discount-api-loading" aria-hidden="true">Loading...</span>';

    function doRender(data) {
      if (ac.signal.aborted) return;
      container.innerHTML = '';
      render(container, data, safePrice, null, currency);
    }

    // Try variant first, then product if no discount (product-level discounts)
    fetchVolumeDiscount(productGid, variantGid, signal || ac.signal).then((data) => {
      if (ac.signal.aborted) return;
      if (data.hasDiscount && data.discounts && data.discounts.length > 0) {
        doRender(data);
        return;
      }
      if (productGid && variantGid) {
        fetchVolumeDiscount(productGid, null, signal || ac.signal).then((productData) => {
          doRender(productData);
        });
      } else {
        doRender(data);
      }
    });
  }

  function initContainer(container) {
    const productGid = container.dataset.productGid;
    const variantGid = container.dataset.variantGid;
    const originalPrice = container.dataset.originalPrice;
    const currency = container.dataset.currency || 'USD';
    if (!productGid && !variantGid) return;
    loadAndRender(container, variantGid, productGid, originalPrice, currency);
  }

  function init() {
    document.querySelectorAll('.volume-discount-api-container').forEach(initContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  const variantChangeUnsub = typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined'
    ? subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = event?.data?.sectionId;
        const variant = event?.data?.variant;
        if (!sectionId) return;
        const container = document.getElementById(`VolumeDiscountApi-${sectionId}`);
        if (!container) return;
        if (container._volumeDiscountAbort) {
          container._volumeDiscountAbort.abort();
        }
        if (variant) {
          const variantGid = `gid://shopify/ProductVariant/${variant.id}`;
          const originalPrice = typeof variant.price === 'number' ? variant.price / 100 : container.dataset.originalPrice;
          if (variant.compare_at_price != null && variant.compare_at_price > 0) {
            container.dataset.compareAtPrice = String(variant.compare_at_price / 100);
          } else {
            delete container.dataset.compareAtPrice;
          }
          container.dataset.variantGid = variantGid;
          container.dataset.originalPrice = originalPrice;
          loadAndRender(container, variantGid, container.dataset.productGid, originalPrice, container.dataset.currency || 'USD');
        } else {
          initContainer(container);
        }
      })
    : null;

  if (variantChangeUnsub && typeof document !== 'undefined') {
    document.addEventListener('page:unload', () => { variantChangeUnsub(); });
  }
})();
